import { ConsentValidityRules } from '@/screens/parts/ConsentValidityRules';
import { PlotConsentDrawer } from '@/screens/parts/PlotConsent';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { renewalStart } from '@/lib/renewals';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import {
  AddButton, Drawer, Field, LiveScreen, QueryState, Select, StatusPill,
  formatDate, todayIso, type Column,
} from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { WithdrawalRegister } from '@/screens/parts/WithdrawalRegister';
import { FileCheck2 } from 'lucide-react';
import { ValidityBadge, addMonths, validityState } from '@/components/ValidityBadge';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows, useSaveRow } from '@/hooks/useResource';
import { partyOptions } from '@/api/lookups';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import { isSupabaseConfigured } from '@/lib/supabase';
import type { ConsentRow } from '@/types/records';
import { CONSENT_TYPES, consentTypeLabel } from '@/lib/consentTypes';
import { LANGUAGES, languageOption } from '@/lib/languages';

const LANGUAGE_OPTIONS = LANGUAGES.map((language) => languageOption(language.code));


/**
 * Digital consent, acknowledged by a one-time code.
 *
 * The farmer receives the code on their OWN registered number and reads it back to the field
 * officer, who enters it. That is what makes the acknowledgement evidence rather than an
 * assertion: the officer cannot produce the code, cannot choose where it is sent, and the
 * database binds that one verified code to that one consent - it cannot be spent twice.
 *
 * Consent is also gated on KYC: the database refuses to acknowledge a consent for a party
 * whose mandatory KYC is not verified, so a missing document surfaces here as a refusal with
 * its reason rather than as a consent that looks complete.
 */
export default function ConsentScreen() {
  const { profile } = useAuth();
  const [tab, setTab] = useState<'consents' | 'withdrawals'>('consents');
  const [issuing, setIssuing] = useState(false);
  const [openConsent, setOpenConsent] = useState<ConsentRow | null>(null);
  const [form, setForm] = useState({ party_id: '', consent_type: 'membership', language_code: 'en', valid_from: todayIso(), valid_to: '' });
  // Renewing (0089): the consent being renewed, when Renewals due sent us here with ?renew=<id>.
  const [renewing, setRenewing] = useState<ConsentRow | null>(null);
  // How long each consent type stays valid, by default (0075).
  const rules = useRows<{ consent_type: string; validity_months: number }>('consent_validity_rules', {});
  const monthsFor = (type: string) => rules.data?.find((rule) => rule.consent_type === type)?.validity_months ?? 12;
  const defaultTo = (type: string, from: string) => (from ? addMonths(from, monthsFor(type)) : '');
  const [stat, setStat] = useState<string | null>(null);

  const consents = useRows<ConsentRow>('party_consents', {
    orderBy: { column: 'created_at', ascending: false },
  });

  const parties = useQuery({
    queryKey: ['picker', 'parties'],
    queryFn: () => partyOptions(),
    enabled: isSupabaseConfigured && issuing,
  });

  const saveConsent = useSaveRow<ConsentRow>('party_consents');

  // One click from Renewals due: the form opens filled in from the consent that is running out,
  // and what is issued records which consent it renews.
  const [params, setParams] = useSearchParams();
  const renewId = params.get('renew');
  useEffect(() => {
    if (!renewId) return;
    const source = (consents.data ?? []).find((row) => row.id === renewId);
    if (!source) return;
    setRenewing(source);
    setForm({
      party_id: source.party_id,
      consent_type: source.consent_type,
      language_code: source.language_code ?? 'en',
      // A renewal starts the day after the old one ends, or today if it has already lapsed.
      valid_from: renewalStart(source.valid_to, todayIso()),
      valid_to: '',
    });
    setIssuing(true);
    params.delete('renew');
    setParams(params, { replace: true });
  }, [renewId, consents.data, params, setParams]);
  const mayIssue = can(profile, 'party.consent', 'create');
  const mayCapture = can(profile, 'party.consent', 'update');

  // Consent keeps its own event lifecycle (issued, acknowledged by code, verified, revoked,
  // lapsed) rather than the four record statuses: each value is a step the farmer took.
  const STATS = useMemo(() => [
    { key: 'all', label: 'Consents', match: () => true, tone: 'neutral' as const },
    { key: 'generated', label: 'Generated', tone: 'info' as const, match: (row: ConsentRow) => row.status === 'generated' },
    { key: 'pending', label: 'Pending', tone: 'warn' as const, match: (row: ConsentRow) => row.status === 'pending' },
    { key: 'acknowledged', label: 'Acknowledged', tone: 'ok' as const, match: (row: ConsentRow) => row.status === 'acknowledged' },
    { key: 'verified', label: 'Verified', tone: 'ok' as const, match: (row: ConsentRow) => row.status === 'verified' },
    { key: 'revoked', label: 'Revoked', tone: 'error' as const, match: (row: ConsentRow) => row.status === 'revoked' },
    { key: 'expired', label: 'Expired', tone: 'info' as const, match: (row: ConsentRow) => row.status === 'expired' || validityState(row.valid_to) === 'expired' },
    { key: 'expiring', label: 'Expiring in 30 days', tone: 'warn' as const, match: (row: ConsentRow) => validityState(row.valid_to) === 'expiring' },
    { key: 'unsigned', label: 'Signed, no copy', tone: 'warn' as const,
      match: (row: ConsentRow) => Boolean(row.acknowledged_at) && !row.has_signed_copy },
    { key: 'withdrawal', label: 'Withdrawals', tone: 'info' as const, match: (row: ConsentRow) => row.consent_type === 'withdrawal' },
  ], []);

  const visible = useMemo(() => applyStat(consents.data ?? [], STATS, stat), [consents.data, STATS, stat]);

  const columns: Array<Column<ConsentRow>> = [
    { key: 'farmer_name', label: 'Farmer', render: (row) => (
      <span className="cell-stack"><strong>{row.farmer_name ?? '--'}</strong><small className="muted">{row.farmer_code ?? ''}</small></span>
    ) },
    { key: 'village_name', label: 'Village', render: (row) => row.village_name ?? '--' },
    { key: 'consent_type', label: 'Consent', render: (row) => (
      <span>{consentTypeLabel(row.consent_type)}{row.withdrawal_code ? <small className="muted"> · {row.withdrawal_code}</small> : null}</span>
    ) },
    { key: 'language_code', label: 'Language', render: (row) => row.language_code.toUpperCase() },
    { key: 'consent_mode', label: 'Mode', render: (row) => (row.consent_mode === 'digital' ? 'Digital (OTP)' : row.consent_mode === 'print' ? 'Print' : '--') },
    { key: 'status', label: 'Status', render: (row) => <StatusPill value={row.status} /> },
    { key: 'acknowledged_at', label: 'Signed on', render: (row) => formatDate(row.acknowledged_at) },
    { key: 'has_signed_copy', label: 'Copy', render: (row) => (row.has_signed_copy
      ? <span className="signed-badge small"><FileCheck2 size={13} /> Signed</span>
      : row.has_draft ? <span className="muted">Draft</span> : <span className="muted">--</span>) },
    { key: 'valid_to', label: 'Validity', render: (row) => <ValidityBadge from={row.valid_from} to={row.valid_to} /> },
    {
      key: 'act',
      label: '',
      render: (row) => (['generated', 'pending'].includes(row.status) && mayCapture ? (
        // Capture opens the consent itself - its two-language draft and print copy, the OTP and the
        // paper route - so no consent is given without the document it agrees to.
        <button onClick={(event) => { event.stopPropagation(); setOpenConsent(row); }} type="button">
          <ShieldCheck size={14} /> Capture
        </button>
      ) : null),
    },
  ];

  async function issue() {
    if (!form.party_id) { toast.error('Choose the party giving consent.'); return; }
    const to = form.valid_to || defaultTo(form.consent_type, form.valid_from);
    if (!form.valid_from || !to || to <= form.valid_from) { toast.error('Give the validity: a start date, and an end date after it.'); return; }
    try {
      await saveConsent.mutateAsync({ values: {
        renews_consent_id: renewing?.id ?? null,
        party_id: form.party_id,
        party_role_context: 'farmer',
        consent_type: form.consent_type,
        language_code: form.language_code,
        status: 'pending',
        valid_from: form.valid_from,
        valid_to: form.valid_to || defaultTo(form.consent_type, form.valid_from),
      } });
      setIssuing(false);
      setRenewing(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be issued.');
    }
  }

  return (
    <LiveScreen
      actions={<AddButton disabled={!mayIssue} label="Issue consent" onClick={() => setIssuing(true)}
                          title={mayIssue ? undefined : 'Your role cannot issue consent.'} />}
      subtitle="Consent issued to a party and acknowledged by a one-time code sent to their own number."
      title="Consent capture"
    >
      <div className="tab-row" role="tablist">
        <button aria-selected={tab === 'consents'} className={tab === 'consents' ? 'active' : ''} onClick={() => setTab('consents')} role="tab" type="button">Consents</button>
        <button aria-selected={tab === 'withdrawals'} className={tab === 'withdrawals' ? 'active' : ''} onClick={() => setTab('withdrawals')} role="tab" type="button">Withdrawal register</button>
      </div>

      {/* How long each consent type is valid by default; moved here when the consent templates page was
          retired (2026-09-28). Changed by a Project Admin, as before. */}
      {tab === 'consents' && <ConsentValidityRules mayEdit={(profile?.tier ?? 99) === 0} />}

      {tab === 'consents' && (
        <>
          <StatCards active={stat} onSelect={setStat} rows={consents.data ?? []} stats={STATS} />
          <QueryState empty="No consent has been issued yet." query={consents}>
            <DataGrid columns={columns} exportName="consents" onRowClick={setOpenConsent} rows={visible}
                      screenKey="party.consent" searchPlaceholder="Farmer, code, village, consent" selectedId={openConsent?.id} />
          </QueryState>
        </>
      )}
      {tab === 'withdrawals' && <WithdrawalRegister onOpenConsent={(consent) => setOpenConsent(consent)} />}

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => { setIssuing(false); setRenewing(null); }} type="button">Cancel</button>
            <button className="primary" onClick={() => void issue()} type="button">Issue</button>
          </>
        }
        onClose={() => { setIssuing(false); setRenewing(null); }}
        open={issuing}
        subtitle="The party is asked to acknowledge it with a code sent to their registered contact."
        title={renewing ? 'Renew consent' : 'Issue consent'}
      >
        {renewing && (
          <p className="renew-note">
            Renewing the <strong>{consentTypeLabel(renewing.consent_type)}</strong> consent
            {renewing.farmer_name ? <> of <strong>{renewing.farmer_name}</strong></> : null}
            {renewing.valid_to ? <>, valid to {formatDate(renewing.valid_to)}</> : null}. The new consent is
            acknowledged by the party's own code, as usual; the old one stays on the record.
          </p>
        )}
        <Field label="Party" required>
          <Select disabled={Boolean(renewing)} onChange={(value) => setForm({ ...form, party_id: value })}
                  options={parties.data ?? []}
                  placeholder={parties.isLoading ? 'Loading...' : 'Select party'}
                  value={form.party_id} />
        </Field>
        <Field label="Consent type" required>
          <Select disabled={Boolean(renewing)} onChange={(value) => setForm({ ...form, consent_type: value, valid_to: '' })} options={CONSENT_TYPES.filter((entry) => entry.id !== 'withdrawal')} value={form.consent_type} />
        </Field>
        <div className="field-row">
          <Field label="Valid from" required>
            <input onChange={(event) => setForm({ ...form, valid_from: event.target.value })} type="date" value={form.valid_from} />
          </Field>
          <Field hint={`Default: ${monthsFor(form.consent_type)} months for this consent type.`} label="Valid to" required>
            <input min={form.valid_from} onChange={(event) => setForm({ ...form, valid_to: event.target.value })} type="date"
                   value={form.valid_to || defaultTo(form.consent_type, form.valid_from)} />
          </Field>
        </div>
        <Field hint="The language the consent is explained and recorded in." label="Language">
          <Select onChange={(value) => setForm({ ...form, language_code: value })} options={LANGUAGE_OPTIONS} value={form.language_code} />
        </Field>
      </Drawer>

      {/* Every consent has its two-language document and both routes (owner, 2026-09-28). */}
      {openConsent && <PlotConsentDrawer consentId={openConsent.id} onClose={() => setOpenConsent(null)} />}
    </LiveScreen>
  );
}

